import React, { useState, useEffect } from 'react';

const DEFAULT_INTERFACES = [
  { id: 'ether1', name: 'ether1', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
  { id: 'ether2', name: 'ether2', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
  { id: 'ether3', name: 'ether3', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
  { id: 'ether4', name: 'ether4', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
  { id: 'ether5', name: 'ether5', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
  { id: 'sfp-sfpplus1', name: 'sfp-sfpplus1', type: 'sfp', comment: '', enabled: true, mtu: 1500, disabled: false },
];

const DEFAULTS = {
  interfaces: DEFAULT_INTERFACES,
};

export default function StepInterface({ data, onChange, onNext, onBack }) {
  const [form, setForm] = useState(data || DEFAULTS);
  const [localEdits, setLocalEdits] = useState({});

  useEffect(() => {
    if (data && data.interfaces) {
      setForm(data);
      setLocalEdits({});
    }
  }, [data]);

  const save = (updated) => {
    setForm(updated);
    onChange(updated);
  };

  const updateIface = (index, key, value) => {
    const ifaces = form.interfaces.map((iface, i) => {
      if (i !== index) return iface;
      const updated = { ...iface, [key]: value };
      return updated;
    });
    save({ ...form, interfaces: ifaces });
  };

  const handleLocalChange = (index, key, value) => {
    const editKey = `${index}-${key}`;
    setLocalEdits(prev => ({ ...prev, [editKey]: value }));
  };

  const handleBlur = (index, key) => {
    const editKey = `${index}-${key}`;
    if (localEdits[editKey] !== undefined) {
      updateIface(index, key, localEdits[editKey]);
      setLocalEdits(prev => {
        const next = { ...prev };
        delete next[editKey];
        return next;
      });
    }
  };

  const getInputValue = (index, key, defaultValue) => {
    const editKey = `${index}-${key}`;
    return localEdits[editKey] !== undefined ? localEdits[editKey] : defaultValue;
  };

  const commitLocalEdits = () => {
    const keys = Object.keys(localEdits);
    if (keys.length === 0) return;

    const ifaces = [...form.interfaces];
    keys.forEach(k => {
      const [indexStr, field] = k.split('-');
      const index = Number(indexStr);
      if (!Number.isInteger(index) || index < 0 || index >= ifaces.length) return;
      if (!field) return;

      let value = localEdits[k];
      if (field === 'mtu') {
        const n = Number(value);
        if (!Number.isNaN(n)) value = n;
      }

      ifaces[index] = { ...ifaces[index], [field]: value };
    });

    setLocalEdits({});
    save({ ...form, interfaces: ifaces });
  };

  const toggleIface = (index) => {
    const ifaces = [...form.interfaces];
    ifaces[index] = { ...ifaces[index], disabled: !ifaces[index].disabled, enabled: ifaces[index].disabled };
    save({ ...form, interfaces: ifaces });
  };

  const addIface = () => {
    const count = form.interfaces.length + 1;
    const newIface = {
      id: 'ether' + count,
      name: 'ether' + count,
      type: 'ether',
      comment: '',
      enabled: true,
      mtu: 1500,
      disabled: false,
    };
    save({ ...form, interfaces: [...form.interfaces, newIface] });
  };

  const removeIface = (index) => {
    if (form.interfaces.length <= 1) return;
    save({ ...form, interfaces: form.interfaces.filter((_, i) => i !== index) });
  };

  const typeIcons = { ether: '🔌', sfp: '💎', bridge: '🔗', vlan: '🔀' };
  const typeColors = { ether: 'bg-blue-500', sfp: 'bg-purple-500', bridge: 'bg-green-500', vlan: 'bg-orange-500' };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🔧 จัดการ Interface (พอร์ต)</h2>
        <p className="text-gray-500 dark:text-gray-400">ตั้งชื่อพอร์ต เปิด/ปิดพอร์ต และตั้งค่า MTU เมื่อเปลี่ยนชื่อพอร์ต ทุกเมนูจะเปลี่ยนตามอัตโนมัติ</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="พอร์ตทั้งหมด" value={form.interfaces.length} icon="🔌" />
        <StatCard label="เปิดใช้งาน" value={form.interfaces.filter(i => !i.disabled).length} icon="✅" />
        <StatCard label="ปิดใช้งาน" value={form.interfaces.filter(i => i.disabled).length} icon="❌" />
        <StatCard label="SFP" value={form.interfaces.filter(i => i.type === 'sfp').length} icon="💎" />
      </div>

      {/* Interface List */}
      <div className="space-y-3 mb-6">
        {form.interfaces.map((iface, index) => (
          <div
            key={iface.id}
            className={`p-4 rounded-xl border-2 transition-all ${
              iface.disabled
                ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 shadow-sm'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 ${typeColors[iface.type] || 'bg-gray-500'} text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-md`}>
                  {typeIcons[iface.type] || '🔌'}
                </span>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white">{iface.name}</span>
                  {iface.comment && <span className="ml-2 text-xs text-gray-400">— {iface.comment}</span>}
                </div>
                {iface.disabled && (
                  <span className="badge bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">ปิดใช้งาน</span>
                )}
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => toggleIface(index)}
                  className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                    iface.disabled
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200'
                  }`}
                >
                  {iface.disabled ? 'เปิด' : 'ปิด'}
                </button>
                <button onClick={() => removeIface(index)} className="btn-danger" disabled={form.interfaces.length <= 1}>ลบ</button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="label text-xs">ชื่อ Interface</label>
                <input
                  type="text"
                  value={getInputValue(index, 'name', iface.name)}
                  onChange={e => handleLocalChange(index, 'name', e.target.value)}
                  onBlur={() => handleBlur(index, 'name')}
                  onKeyDown={e => { if (e.key === 'Enter') handleBlur(index, 'name'); }}
                  className="input-field text-sm"
                  placeholder="เช่น WAN, LAN, Uplink"
                />
              </div>
              <div>
                <label className="label text-xs">ประเภท</label>
                <select
                  value={iface.type}
                  onChange={e => updateIface(index, 'type', e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="ether">Ethernet</option>
                  <option value="sfp">SFP/SFP+</option>
                  <option value="bridge">Bridge</option>
                  <option value="vlan">VLAN</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">MTU</label>
                <input
                  type="number"
                  min="68"
                  max="9000"
                  value={getInputValue(index, 'mtu', iface.mtu)}
                  onChange={e => handleLocalChange(index, 'mtu', e.target.value)}
                  onBlur={() => handleBlur(index, 'mtu')}
                  onKeyDown={e => { if (e.key === 'Enter') handleBlur(index, 'mtu'); }}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="label text-xs">หมายเหตุ</label>
                <input
                  type="text"
                  value={getInputValue(index, 'comment', iface.comment)}
                  onChange={e => handleLocalChange(index, 'comment', e.target.value)}
                  onBlur={() => handleBlur(index, 'comment')}
                  onKeyDown={e => { if (e.key === 'Enter') handleBlur(index, 'comment'); }}
                  className="input-field text-sm"
                  placeholder="เช่น เชื่อมต่อ ISP"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addIface} className="mb-6 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-slate-500 hover:text-slate-500 transition-colors w-full font-medium text-sm">
        + เพิ่ม Interface
      </button>

      <div className="p-3 bg-slate-50 dark:bg-slate-900/10 border border-slate-200 dark:border-slate-800/50 rounded-lg mb-6">
        <p className="text-xs text-slate-700 dark:text-slate-300">
          <strong>💡 เคล็ดลับ:</strong> เมื่อเปลี่ยนชื่อ Interface (เช่น ether1 → WAN) ชื่อจะเปลี่ยนอัตโนมัติในทุกเมนูที่เลือกพอร์ตนี้ เช่น หน้าตั้งค่า Internet, VLAN, DHCP
        </p>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={() => { commitLocalEdits(); onBack(); }} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={() => { commitLocalEdits(); onNext(); }} className="btn-primary">ถัดไป ({form.interfaces.length} พอร์ต)</button>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center">
      <div className="text-xl mb-1">{icon}</div>
      <div className="text-lg font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}
